import React, { useMemo, useState, useEffect, lazy, Suspense, useRef } from 'react';
import '../../../styles/sections/video/VideoGallerySection.css';
import { VideoManagerProvider } from '../../../components/media/video/VideoManager';

// Lazy load del componente pesado - solo se carga cuando es necesario
const VideoCarouselRow = lazy(() => import('./VideoCarouselRow'));

const Row = ({ items = [], aspect = 'horizontal', animationDuration = 60, shouldDuplicate = true }) => (
  <Suspense fallback={<div className="video-carousel-row-skeleton" />}>
    <VideoCarouselRow 
      items={items} 
      aspect={aspect} 
      animationDuration={animationDuration} 
      shouldDuplicate={shouldDuplicate}
    />
  </Suspense>
);

const VideoGallerySection = ({ title = 'Nuestros mejores videos', subtitle = 'Deslizá para ver más trabajos', horizontalItems = [], verticalItems = [], animationDuration = 60 }) => {
  // OPTIMIZACIÓN CRÍTICA: Renderizar inmediatamente para que los thumbnails carguen sin delay
  // Los videos NO se cargan hasta que el usuario interactúa, pero los thumbnails sí
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const sectionRef = useRef(null);
  
  // Intersection Observer para cargar videos adicionales cuando la sección está visible
  // OPTIMIZACIÓN: Renderizar inmediatamente pero cargar videos adicionales progresivamente
  useEffect(() => {
    if (!sectionRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Cuando la sección está visible, cargar videos adicionales progresivamente
          if (entry.isIntersecting && !initialLoadComplete) {
            // Cargar videos adicionales después de un breve delay para no bloquear
            // Los thumbnails ya se están cargando inmediatamente
            // Usar requestIdleCallback para cargar videos adicionales cuando el navegador esté idle
            if ('requestIdleCallback' in window) {
              requestIdleCallback(() => {
                setInitialLoadComplete(true);
              }, { timeout: 2000 }); // Timeout más corto (2 segundos)
            } else {
              // Fallback: esperar 1 segundo después de que la sección sea visible
              setTimeout(() => {
                setInitialLoadComplete(true);
              }, 1000);
            }
          }
        });
      },
      {
        root: null,
        rootMargin: '200px', // Pre-cargar cuando está a 200px del viewport
        threshold: 0.1 // Cuando al menos 10% está visible
      }
    );

    observer.observe(sectionRef.current);

    return () => observer.disconnect();
  }, [initialLoadComplete]);

  // Split horizontal items into 2 balanced rows
  // OPTIMIZACIÓN: Renderizar inmediatamente para que los thumbnails carguen sin delay
  const [row1, row2, allRow1, allRow2] = useMemo(() => {
    const total = horizontalItems.length;
    if (total === 0) return [[], [], [], []];

    const midPoint = Math.ceil(total / 2);
    const h1 = horizontalItems.slice(0, midPoint);
    const h2 = horizontalItems.slice(midPoint);
    
    // Para carga inicial, mostrar más videos para mejor UX
    // Los thumbnails se cargan inmediatamente sin afectar el rendimiento
    const initialCount = Math.min(6, total); // Mostrar hasta 6 videos inicialmente
    const initialH1 = h1.slice(0, Math.ceil(initialCount / 2));
    const initialH2 = h2.slice(0, Math.floor(initialCount / 2));
    
    return [initialH1, initialH2, h1, h2];
  }, [horizontalItems]);

  // Split vertical items into 2 balanced rows
  // OPTIMIZACIÓN: Renderizar inmediatamente para que los thumbnails carguen sin delay
  const [row3, row4, allRow3, allRow4] = useMemo(() => {
    const total = verticalItems.length;
    if (total === 0) return [[], [], [], []];

    const midPoint = Math.ceil(total / 2);
    const v1 = verticalItems.slice(0, midPoint);
    const v2 = verticalItems.slice(midPoint);
    
    // Para carga inicial, mostrar más videos para mejor UX
    // Los thumbnails se cargan inmediatamente sin afectar el rendimiento
    const initialCount = Math.min(6, total); // Mostrar hasta 6 videos inicialmente
    const initialV1 = v1.slice(0, Math.ceil(initialCount / 2));
    const initialV2 = v2.slice(0, Math.floor(initialCount / 2));
    
    return [initialV1, initialV2, v1, v2];
  }, [verticalItems]);

  // Cargar el resto de videos progresivamente después de que los thumbnails iniciales se carguen
  // OPTIMIZACIÓN: Cargar videos adicionales después de un breve delay para no bloquear la carga inicial
  // Los thumbnails ya se están cargando inmediatamente, así que no hay problema
  useEffect(() => {
    // Cargar videos adicionales después de un breve delay
    // Esto permite que los thumbnails iniciales se carguen primero
    const loadRemainingVideos = () => {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => {
          setInitialLoadComplete(true);
        }, { timeout: 1500 }); // Timeout más corto (1.5 segundos)
      } else {
        // Fallback: esperar 1 segundo después de que la sección se renderice
        setTimeout(() => {
          setInitialLoadComplete(true);
        }, 1000);
      }
    };

    // Cargar videos adicionales después de un breve delay
    // Los thumbnails ya se están cargando inmediatamente
    const timeoutId = setTimeout(loadRemainingVideos, 500); // Delay muy corto (500ms)

    return () => clearTimeout(timeoutId);
  }, []);

  // Usar todos los videos después de la carga inicial, o solo los iniciales
  const finalRow1 = initialLoadComplete ? allRow1 : row1;
  const finalRow2 = initialLoadComplete ? allRow2 : row2;
  const finalRow3 = initialLoadComplete ? allRow3 : row3;
  const finalRow4 = initialLoadComplete ? allRow4 : row4;

  return (
    <VideoManagerProvider>
      <section className="video-gallery" ref={sectionRef}>
        <div className="video-gallery__header">
          <h2 className="video-gallery__title">{title}</h2>
          {subtitle && <p className="video-gallery__subtitle">{subtitle}</p>}
        </div>
        {/* OPTIMIZACIÓN: Renderizar inmediatamente para que los thumbnails carguen sin delay */}
        <div className="video-gallery__grid">
          <Row items={finalRow1} aspect="horizontal" animationDuration={120} shouldDuplicate={initialLoadComplete} />
          <Row items={finalRow2} aspect="horizontal" animationDuration={120} shouldDuplicate={initialLoadComplete} />
          <Row items={finalRow3} aspect="vertical" animationDuration={45} shouldDuplicate={initialLoadComplete} />
          <Row items={finalRow4} aspect="vertical" animationDuration={45} shouldDuplicate={initialLoadComplete} />
        </div>
      </section>
    </VideoManagerProvider>
  );
};

export default VideoGallerySection;
